import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SerializeModuleService } from '../edit/serialize-module.service';
import { of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ModulesSaveResultEnum } from './models/modules-save-result.enum';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "../edit/serialize-module.service";
// #NOTE: [EM] This assumes services on qa and scene-editor
// #TODO: [EM] Make this service API explicit about qa vs production
const cdbURL = 'cdb/content';
const cdbCopyURL = `cdb/copy/content`;
const formHeaders = {
    headers: new HttpHeaders({
        'Content-Type': 'application/json; charset=utf-8'
    })
};
// top-level directories we wish to expose to content creators directly
const primaryCategories = [
    'production',
    'client',
    'preview',
    'internal_content',
    'public_preview',
    'demos',
    'social',
    'exports'
];
export class ModulesService {
    constructor(http, serializeModuleService) {
        this.http = http;
        this.serializeModuleService = serializeModuleService;
    }
    // write the story module to the modules server
    saveModule(story, modulePath) {
        modulePath = this.formatModulePath(modulePath);
        const url = [cdbURL, 'modules', modulePath].join('/');
        // !important: custom stringify, escape non-ASCII unicode sequences
        const storyJSONString = this.JSONStringify(story);
        return this.http.put(url, storyJSONString, formHeaders).pipe(
        // success    
        map(() => ModulesSaveResultEnum.Success), 
        // http status 201 | 202 treated as "error" by Angular HTTP, handle server response
        catchError(({ status }) => {
            if (status >= 200 && status <= 202) {
                return of(ModulesSaveResultEnum.Success);
            }
            else {
                return of(ModulesSaveResultEnum.Fail);
            }
        }));
    }
    // Post a request to make a hard copy of content state directory
    copyState(sourceId, targetId) {
        const url = [cdbCopyURL, `states`, targetId].join('/');
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json; charset=utf-8',
                'source-state': sourceId
            })
        };
        return this.http.put(url, {}, options);
    }
    listContents(primaryCategory, secondaryCategory, name) {
        const path = this.generateModulePath(primaryCategory, secondaryCategory || '', name || '');
        return this.http.get(`/cdb/ls/content/modules/${path}`)
            .pipe(map((results) => {
            return results.contents;
        }));
    }
    getState(id) {
        return this.http.get(`/cdb/ls/content/states/${id}`)
            .pipe(map((results) => {
            return results.contents;
        }));
    }
    // return list of BioDigital Client directories
    getClients() {
        return this.http.get(`/search/client`)
            .pipe(map((response) => {
            return response.clients;
        }));
    }
    // Iterates through an entire scene and determine what states are in use by all the assets.
    // A very thorough check resulting in quite a limited output.
    getUsedStates() {
        const states = {};
        Object.values(Human.scene.objects)
            .reduce((result, object) => {
            ['transformId', 'morphId', 'geometryId', 'materialId'].forEach(property => {
                const state = this.getPropertyState(object, property);
                if (state.length) {
                    states[state] = true;
                }
            });
            if (object.hasOwnProperty('renderables')) {
                ['morphId', 'geometryId', 'materialId'].forEach(property => {
                    const renderables = object.renderables;
                    renderables.forEach((renderable) => {
                        const state = this.getPropertyState(renderable, property);
                        if (state.length) {
                            states[state] = true;
                        }
                    });
                });
            }
            return result;
        }, {});
        return Object.keys(states);
    }
    // Get the semantically meaningful parts of a module path
    parseScenePath(id) {
        const parts = id.split('/');
        if (parts.length === 1) {
            // invalid scene path
            return null;
        }
        // # NOTE: [EM] hardcoded content environment
        const urlSubDomain = "qa" /* qa */;
        return {
            fullPath: `https://${urlSubDomain}.biodigital.com/content/modules/${id}`,
            relativePath: parts.slice(0, parts.length - 1).join('/'),
            sceneName: parts[parts.length - 1]
        };
    }
    // ensure the moduleId has a .json extension
    formatModulePath(modulePath) {
        let result = modulePath;
        if (!/\.json$/.test(modulePath)) {
            result = `${result}.json`;
        }
        return result;
    }
    // Return a list of primary modules categories
    getPrimaryCategories() {
        return of(primaryCategories);
    }
    // Return a list of secondary catogories, given a primary category
    getSecondaryCategories(primaryCategory) {
        switch (primaryCategory) {
            case 'production': {
                // list gender categories
                return of(['maleAdult', 'femaleAdult']);
            }
            case 'client': {
                // list client-projects
                return this.getClients();
            }
            default: {
                // no secondary category available
                return of([]);
            }
        }
    }
    generateSaveModule(id) {
        const scenePath = this.parseScenePath(id);
        const categories = scenePath.relativePath.split('/');
        return {
            id: id,
            name: scenePath.sceneName,
            primaryCategory: categories[0] || '',
            secondaryCategory: categories[1] || ''
        };
    }
    generateModulePath(primaryCategory, secondaryCategory, name) {
        const path = [];
        if (primaryCategory.length) {
            path.push(primaryCategory);
        }
        if (secondaryCategory.length) {
            path.push(secondaryCategory);
        }
        if (name.length) {
            if (name.indexOf('.json') < 0) {
                name = `${name}.json`;
            }
            path.push(name);
        }
        return path.join('/');
    }
    getPropertyState(object, property) {
        if (object[property] != null) {
            return object[property].split('/')[0];
        }
        return '';
    }
    // #TODO: centralize/reuse
    JSONStringify(object) {
        return JSON.stringify(object)
            .replace(/[\u007f-\uffff]/g, (character) => {
            return '\\u' + ('0000' + character.charCodeAt(0).toString(16)).slice(-4);
        });
    }
}
ModulesService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function ModulesService_Factory() { return new ModulesService(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.SerializeModuleService)); }, token: ModulesService, providedIn: "root" });
